import React from 'react';

import { Spinner } from '../../components/Spinner';

export interface Column<T> {
  key: keyof T | string;
  title?: React.ReactNode;
  width?: string;
  sticky?: boolean;
  render?: (item: T) => React.ReactNode;
  borders?: {
    left?: boolean;
    right?: boolean;
    bottom?: boolean;
  };
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

interface TableProps<
  T extends { id: string | number; didMissCheckins?: boolean },
> {
  columns: Column<T>[];
  data: T[];
  className?: string;
  stickyHeader?: boolean;
  borderStyle?: 'solid' | 'dashed';
  onRowClick?: (item: T) => void;
  containerClassName?: string;
  isLoading?: boolean;
  error?: Error | null;
  emptyStateMessage?: string;
}

const getAlignmentClass = (verticalAlign?: string): string => {
  switch (verticalAlign) {
    case 'top':
      return 'align-top';
    case 'bottom':
      return 'align-bottom';
    default:
      return 'align-middle';
  }
};

export function Table<
  T extends { id: string | number; didMissCheckins?: boolean },
>({
  columns,
  data,
  className = '',
  stickyHeader = false,
  borderStyle = 'solid',
  onRowClick,
  containerClassName,
  isLoading = false,
  error = null,
  emptyStateMessage = 'No data available',
}: TableProps<T>): JSX.Element {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full min-h-[200px]">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full min-h-[200px] text-red-500">
        {error.message || 'Error loading data'}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <table
        className={`w-full border-collapse ${className} `}
        style={{ tableLayout: 'fixed' }}
      >
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key as string}
                style={{ width: column.width }}
                className={`
                  ${stickyHeader ? 'sticky top-0 bg-white z-10' : ''}
                  ${column.sticky ? 'sticky left-0 bg-white z-20' : ''}
                  font-normal opacity-80 py-4 text-[#8C8C8C] text-sm
                  ${column.align ? `text-${column.align}` : 'text-left'}
                `}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="border-t border-[var(--Border-border-gray-light,#F0F0F0)]">
          {data.length === 0 ? (
            <tr className="hover:bg-gray-50">
              <td
                colSpan={columns.length}
                className="text-center py-8 text-gray-500 bg-gray-50/50"
              >
                <div className="flex flex-col items-center gap-2">
                  <span className="text-sm font-medium">
                    {emptyStateMessage}
                  </span>
                </div>
              </td>
            </tr>
          ) : (
            data.map((item, rowIndex) => (
              <tr
                key={item.id}
                onClick={() => onRowClick?.(item)}
                className={`p-2  ${onRowClick ? 'cursor-pointer' : ''} ${
                  item?.didMissCheckins ? 'bg-primary-light' : ''
                }`}
              >
                {columns.map((column) => (
                  <td
                    key={`${item.id}-${column.key as string}`}
                    className={`
                    ${column.sticky ? 'sticky left-0 bg-white z-10' : ''}
                    ${
                      column.borders?.left
                        ? `border-l border-${borderStyle} border-gray-300`
                        : ''
                    }
                    ${
                      column.borders?.right
                        ? `border-r border-${borderStyle} border-gray-300`
                        : ''
                    }
                    ${
                      column.borders?.bottom && rowIndex !== data.length - 1
                        ? `border-b border-${borderStyle} border-gray-200`
                        : ''
                    }
                    ${column.align ? `text-${column.align}` : 'text-left'}
                    py-3 px-2 ${getAlignmentClass(column.verticalAlign)}
                  `}
                  >
                    {column.render
                      ? column.render(item)
                      : String(
                          (item as Record<string, unknown>)[
                            column.key as string
                          ],
                        )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
